import { getDB, getCollection } from '@/rxdb';
import { queueOperation } from '@/rxdb/offlineSync';
import { getCurrentUserScope, getOfflineEntityId } from '@/rxdb/sessionScope';

function createClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function upsertLocal(collectionName, record) {
  const db = await getDB();
  const collection = getCollection(db, collectionName);
  return collection.upsert(record);
}

export async function findLocal(collectionName, query = {}) {
  const db = await getDB();
  const collection = getCollection(db, collectionName);
  const documents = await collection.find(query).exec();
  return documents.map(document => document.toJSON());
}

export async function findLocalById(collectionName, id) {
  const db = await getDB();
  const collection = getCollection(db, collectionName);
  const document = await collection.findOne(id).exec();
  return document ? document.toJSON() : null;
}

export async function removeLocal(collectionName, id) {
  const db = await getDB();
  const collection = getCollection(db, collectionName);
  const document = await collection.findOne(id).exec();
  if (document) await document.remove();
}

export async function pullToLocal(collectionName, records, mapRecord) {
  const db = await getDB();
  const collection = getCollection(db, collectionName);
  const mappedRecords = records.map(record => mapRecord(record));
  await collection.bulkUpsert(mappedRecords);
  return mappedRecords;
}

export function createOfflineRepository({ entityType, url, method = 'POST', dedupeKey = null }) {
  if (!entityType || !url) throw new Error('entityType and url are required');

  async function save(data, operation = 'update') {
    const db = await getDB();
    const ownerId = getCurrentUserScope();
    if (!ownerId) throw new Error('An authenticated user scope is required for offline changes.');
    const now = new Date().toISOString();
    const clientId = data.clientId || data.studentId || data.id || (data.Id > 0 ? data.Id : null) || createClientId();
    const localId = getOfflineEntityId(entityType, clientId, ownerId);
    const entityKey = localId;
    const local = {
      id: localId,
      entityType,
      ownerId,
      data: { ...data, clientId },
      operation,
      syncStatus: 'pending',
      syncAttempts: 0,
      lastSyncAttempt: null,
      lastSyncedAt: null,
      nextRetryAt: null,
      syncError: null,
      serverId: data.serverId || null,
      serverUpdatedAt: null,
      version: data.version || null,
      clientUpdatedAt: now,
      updatedAt: now
    };
    await db.offlineEntities.upsert(local);
    const requestUrl = typeof url === 'function' ? url(local.data, operation) : url;
    const requestMethod = typeof method === 'function' ? method(local.data, operation) : method;
    await queueOperation({
      id: `${entityType}:${clientId}:${now}`,
      operationType: 'http',
      entityKey,
      method: requestMethod,
      url: requestUrl,
      payload: local.data,
      collectionName: 'offlineEntities',
      localRecordId: local.id,
      clientUpdatedAt: now
    });
    return local.data;
  }

  return {
    create: data => save(data, 'create'),
    update: data => save(data, 'update'),
    remove: data => save({ ...data, clientId: data.clientId || data.id }, 'delete'),
    cache: async data => {
      const db = await getDB();
      const ownerId = getCurrentUserScope();
      if (!ownerId) throw new Error('An authenticated user scope is required for offline changes.');
      const now = new Date().toISOString();
      const clientId = data.clientId || data.id || data.studentId;
      if (clientId === undefined || clientId === null) throw new Error('A record identifier is required to cache offline data.');
      const localId = getOfflineEntityId(entityType, clientId, ownerId);
      const existing = await db.offlineEntities.findOne(localId).exec();
      if (existing && ['pending', 'syncing', 'failed'].includes(existing.toJSON().syncStatus)) {
        return existing.toJSON().data;
      }
      // A record created offline has a client-generated id; its server copy arrives under the server id.
      const key = dedupeKey ? dedupeKey(data) : null;
      if (key) {
        const siblings = (await findLocal('offlineEntities', { selector: { entityType, ownerId } }))
          .filter(record => record.id !== localId && dedupeKey(record.data) === key);
        if (siblings.some(record => ['pending', 'syncing', 'failed'].includes(record.syncStatus))) {
          return siblings[0].data;
        }
        for (const record of siblings) await removeLocal('offlineEntities', record.id);
      }
      const local = {
        id: localId,
        entityType,
        ownerId,
        data: { ...data, clientId },
        operation: 'update',
        syncStatus: 'synced',
        syncAttempts: 0,
        lastSyncAttempt: null,
        lastSyncedAt: now,
        nextRetryAt: null,
        syncError: null,
        serverId: data.serverId || data.id || data.studentId || null,
        serverUpdatedAt: now,
        version: data.version || null,
        clientUpdatedAt: now,
        updatedAt: now
      };
      await db.offlineEntities.upsert(local);
      return local.data;
    },
    get: id => findLocalById('offlineEntities', getOfflineEntityId(entityType, id)),
    list: async query => {
      const ownerId = getCurrentUserScope();
      if (!ownerId) return [];
      const records = await findLocal('offlineEntities', {
        selector: { entityType, ownerId, ...(query || {}) },
        sort: [{ updatedAt: 'desc' }]
      });
      return records.map(record => ({
        ...record.data,
        syncStatus: record.syncStatus,
        syncError: record.syncError,
        clientId: record.data.clientId || record.id
      }));
    }
  };
}
